import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Clock, Info, Luggage, MapPin, Users, X } from "lucide-react";
import { api, mediaUrl, publicMedia } from "@/api";
import { pick, useLang } from "@/i18n";
import { BookingDialog } from "@/components/BookingDialog";
import { GoldImage } from "@/components/GoldImage";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

export default function PackageDetail() {
    const { id } = useParams();
    const { lang, t } = useLang();
    const [pkg, setPkg] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [bookingOpen, setBookingOpen] = useState(false);
    const [activeImg, setActiveImg] = useState(0);

    useEffect(() => {
        setPkg(null);
        setNotFound(false);
        setActiveImg(0);
        api.get(`/public/packages/${id}`).then((r) => setPkg(r.data)).catch(() => setNotFound(true));
    }, [id]);

    if (notFound) {
        return (
            <div className="marble-bg min-h-screen pt-40 pb-24 text-center" data-testid="package-not-found">
                <p className="text-stone-400">{t.pkg.notFound}</p>
                <Link to="/esperienze" className="mt-6 inline-block text-amber-300 text-sm underline underline-offset-4">{t.pkg.back}</Link>
            </div>
        );
    }
    if (!pkg) return <div className="marble-bg min-h-screen pt-40 text-center text-stone-500">{t.pkg.loading}</div>;

    const gallery = [
        ...(pkg.cover_media_id ? [mediaUrl(pkg.cover_media_id)] : pkg.cover_image_url ? [publicMedia(pkg.cover_image_url)] : []),
        ...(pkg.gallery_media_ids || []).map(mediaUrl),
        ...(pkg.gallery || []).map((g) => publicMedia(g)),
    ];
    const name = pick(pkg.name, lang);
    const instantOk = pkg.allow_instant_booking && pkg.price > 0;

    return (
        <div data-testid="package-detail-page" className="marble-bg min-h-screen pt-28 pb-24">
            <Seo lang={lang} title={`${name} — TOURVOYAGERS`} description={pick(pkg.short_description, lang)} />
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <Link to={pkg.category?.startsWith("transfer-") ? "/transfer" : "/esperienze"} data-testid="package-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-stone-400 hover:text-amber-300 transition-colors">
                    <ArrowLeft size={14} /> {t.pkg.back}
                </Link>

                <div className="mt-8 grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <Reveal>
                            {pkg.code && <p className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-medium">{t.pkg.code}: {pkg.code}</p>}
                            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-amber-50 leading-tight" data-testid="package-title">{name}</h1>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <div className="mt-8 overflow-hidden rounded-xl border border-amber-500/25" data-testid="package-gallery">
                                <GoldImage
                                    src={gallery[activeImg] || null}
                                    alt={name}
                                    label={pkg.code || pkg.category}
                                    className="aspect-[16/10] w-full object-cover"
                                />
                            </div>
                            {gallery.length > 1 && (
                                <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                                    {gallery.map((g, i) => (
                                        <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setActiveImg(i)} className={`shrink-0 overflow-hidden rounded-lg border transition-colors ${i === activeImg ? "border-amber-400" : "border-amber-500/20 opacity-60 hover:opacity-100"}`}>
                                            <GoldImage src={g} alt={`${name} ${i + 1}`} className="h-16 w-24 object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </Reveal>

                        <Reveal delay={0.15}>
                            <p className="mt-10 text-base text-stone-300 leading-relaxed whitespace-pre-line" data-testid="package-description">{pick(pkg.description, lang) || pick(pkg.short_description, lang)}</p>
                        </Reveal>

                        {pkg.itinerary?.length > 0 && (
                            <Reveal delay={0.2}>
                                <h2 className="mt-12 font-display text-2xl text-amber-100">{t.pkg.itinerary}</h2>
                                <ol className="mt-6 space-y-5" data-testid="package-itinerary">
                                    {pkg.itinerary.map((step, i) => (
                                        <li key={i} className="flex gap-4">
                                            {step.time ? (
                                                <span className="flex h-8 min-w-[4.5rem] shrink-0 items-center justify-center rounded-full border border-amber-500/40 px-2 font-mono-accent text-xs text-amber-300">{step.time}</span>
                                            ) : (
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-500/40 font-mono-accent text-xs text-amber-300">{String(i + 1).padStart(2, "0")}</span>
                                            )}
                                            <div className="pt-1">
                                                <p className="text-sm font-semibold text-stone-200">{step.title !== undefined ? pick(step.title, lang) : pick(step, lang)}</p>
                                                {step.desc && pick(step.desc, lang) && <p className="mt-1 text-sm text-stone-400">{pick(step.desc, lang)}</p>}
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </Reveal>
                        )}

                        {pkg.stops?.length > 0 && (
                            <Reveal delay={0.22}>
                                <h2 className="mt-10 font-display text-2xl text-amber-100">{t.pkg.stops}</h2>
                                <ul className="mt-4 space-y-2 text-sm text-stone-300">
                                    {pkg.stops.map((s, i) => <li key={i} className="flex gap-2"><span className="text-amber-400">◆</span>{pick(s, lang)}</li>)}
                                </ul>
                            </Reveal>
                        )}

                        {pkg.optional_stop_allowed && (
                            <Reveal delay={0.24}>
                                <div className="mt-8 flex items-start gap-3 rounded-lg border border-amber-500/20 bg-black/40 p-4 text-sm text-stone-300" data-testid="package-optional-stop">
                                    <Info size={16} className="mt-0.5 shrink-0 text-amber-400" />
                                    {t.pkg.optionalStop}
                                </div>
                            </Reveal>
                        )}

                        <div className="mt-12 grid gap-8 sm:grid-cols-2">
                            {pkg.included?.length > 0 && (
                                <Reveal delay={0.26}>
                                    <h2 className="font-display text-xl text-amber-100">{t.pkg.included}</h2>
                                    <ul className="mt-4 space-y-2.5 text-sm text-stone-300" data-testid="package-included">
                                        {pkg.included.map((s, i) => <li key={i} className="flex gap-2.5"><Check size={15} className="mt-0.5 shrink-0 text-amber-400" />{pick(s, lang)}</li>)}
                                    </ul>
                                </Reveal>
                            )}
                            {pkg.excluded?.length > 0 && (
                                <Reveal delay={0.28}>
                                    <h2 className="font-display text-xl text-amber-100">{t.pkg.excluded}</h2>
                                    <ul className="mt-4 space-y-2.5 text-sm text-stone-400" data-testid="package-excluded">
                                        {pkg.excluded.map((s, i) => <li key={i} className="flex gap-2.5"><X size={15} className="mt-0.5 shrink-0 text-stone-500" />{pick(s, lang)}</li>)}
                                    </ul>
                                </Reveal>
                            )}
                        </div>

                        {(pkg.included_transfers?.length > 0 || pkg.included_stays?.length > 0 || pkg.included_experiences?.length > 0) && (
                            <Reveal delay={0.29}>
                                <h2 className="mt-12 font-display text-2xl text-amber-100">{t.pkg.svcTitle}</h2>
                                <div className="mt-5 grid gap-6 sm:grid-cols-3" data-testid="package-services">
                                    {[
                                        [pkg.included_transfers, t.pkg.svcTransfers, "transfers"],
                                        [pkg.included_stays, t.pkg.svcStays, "stays"],
                                        [pkg.included_experiences, t.pkg.svcExperiences, "experiences"],
                                    ].map(([list, label, key]) => list?.length > 0 && (
                                        <div key={key} className="rounded-xl border border-amber-500/15 bg-black/30 p-5" data-testid={`package-svc-${key}`}>
                                            <h3 className="text-[10px] uppercase tracking-[0.2em] text-amber-400/90 font-semibold">{label}</h3>
                                            <ul className="mt-3 space-y-2 text-sm text-stone-300">
                                                {list.map((s, i) => <li key={i} className="flex gap-2"><Check size={14} className="mt-0.5 shrink-0 text-amber-400" />{pick(s, lang)}</li>)}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </Reveal>
                        )}

                        <div className="mt-12 space-y-8">
                            {pick(pkg.conditions, lang) && (
                                <Reveal delay={0.3}>
                                    <h2 className="font-display text-xl text-amber-100">{t.pkg.conditions}</h2>
                                    <p className="mt-3 text-sm text-stone-400 leading-relaxed">{pick(pkg.conditions, lang)}</p>
                                </Reveal>
                            )}
                            {pick(pkg.cancellation, lang) && (
                                <Reveal delay={0.32}>
                                    <h2 className="font-display text-xl text-amber-100">{t.pkg.cancellation}</h2>
                                    <p className="mt-3 text-sm text-stone-400 leading-relaxed">{pick(pkg.cancellation, lang)}</p>
                                </Reveal>
                            )}
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <Reveal delay={0.15} className="lg:sticky lg:top-28">
                            <div className="gold-card rounded-xl p-8" data-testid="package-booking-panel">
                                {pkg.price != null && (
                                    <div>
                                        <p className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80">{t.pkg.price}</p>
                                        <p className="mt-1 font-display text-4xl gold-text" data-testid="package-price">€{pkg.price.toFixed(2)}</p>
                                        {(pkg.pricing_type || pkg.pricing_type === "") && (
                                            <p className="text-xs text-stone-500" data-testid="package-pricing-type">
                                                {pkg.pricing_type === "persona" ? t.pkg.perPerson : pkg.pricing_type === "servizio" || !pkg.pricing_type ? t.pkg.perService : pkg.pricing_type}
                                            </p>
                                        )}
                                    </div>
                                )}
                                <dl className="mt-8 space-y-4 text-sm">
                                    {pkg.destination && (
                                        <div className="flex items-center justify-between border-b border-amber-500/10 pb-3">
                                            <dt className="flex items-center gap-2 text-stone-400"><MapPin size={15} className="text-amber-500/70" />{t.pkg.destination}</dt>
                                            <dd className="text-stone-200" data-testid="package-destination">{pkg.destination}</dd>
                                        </div>
                                    )}
                                    {pkg.category && (
                                        <div className="flex items-center justify-between border-b border-amber-500/10 pb-3">
                                            <dt className="text-stone-400">{t.pkg.categoryLabel}</dt>
                                            <dd className="text-right text-stone-200">{pkg.category}</dd>
                                        </div>
                                    )}
                                    {(pkg.duration_label || pkg.duration_hours != null) && (
                                        <div className="flex items-center justify-between border-b border-amber-500/10 pb-3">
                                            <dt className="flex items-center gap-2 text-stone-400"><Clock size={15} className="text-amber-500/70" />{t.pkg.duration}</dt>
                                            <dd className="text-stone-200">{pick(pkg.duration_label, lang) || `${pkg.duration_hours} ${pkg.duration_hours === 1 ? t.pkg.hour : t.pkg.hours}`}</dd>
                                        </div>
                                    )}
                                    {pkg.max_passengers != null && (
                                        <div className="flex items-center justify-between border-b border-amber-500/10 pb-3">
                                            <dt className="flex items-center gap-2 text-stone-400"><Users size={15} className="text-amber-500/70" />{t.pkg.maxPassengers}</dt>
                                            <dd className="text-stone-200">{pkg.max_passengers}</dd>
                                        </div>
                                    )}
                                    {pkg.luggage_capacity != null && pkg.luggage_capacity > 0 && (
                                        <div className="flex items-center justify-between border-b border-amber-500/10 pb-3">
                                            <dt className="flex items-center gap-2 text-stone-400"><Luggage size={15} className="text-amber-500/70" />{t.pkg.luggage}</dt>
                                            <dd className="text-stone-200">{pkg.luggage_capacity}</dd>
                                        </div>
                                    )}
                                    {pkg.vehicle_class && (
                                        <div className="flex items-center justify-between border-b border-amber-500/10 pb-3">
                                            <dt className="text-stone-400">{t.pkg.vehicle}</dt>
                                            <dd className="text-right text-stone-200">{pkg.vehicle_class}</dd>
                                        </div>
                                    )}
                                </dl>

                                <p className="mt-6 text-xs leading-relaxed text-stone-500">{instantOk ? t.pkg.instantNote : t.pkg.manualNote}</p>

                                <button
                                    data-testid="package-book-request-button"
                                    onClick={() => setBookingOpen(true)}
                                    className="mt-6 w-full rounded-full border border-amber-500/50 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-amber-200 transition-colors hover:bg-amber-400/10"
                                >
                                    {t.pkg.bookRequest}
                                </button>
                                {instantOk && (
                                    <button
                                        data-testid="package-book-instant-button"
                                        onClick={() => setBookingOpen(true)}
                                        className="mt-3 w-full rounded-full bg-amber-400 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_24px_rgba(212,175,55,0.45)]"
                                    >
                                        {t.pkg.bookInstant}
                                    </button>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>

            <BookingDialog pkg={pkg} open={bookingOpen} onOpenChange={setBookingOpen} />
        </div>
    );
}
